import { useAssistContext } from "@/components/Assist/AssistContext";
import { getInitialChatName } from "@/lib/chat-name";
import { SelectedExternalFile } from "@/lib/external-file-browser";
import { groupKitChatControllerGenerateConversationName } from "@/lib/gen-brain";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useMessagesData } from "../data/use-messages-data";
import { useGroupUuid } from "../hooks/use-group-uuid";
import {
  chatLibraryFileControllerAttachFile,
  groupKitChatControllerCreateChat,
  groupKitControllerCreateKit,
  groupKitControllerUpdateKit,
} from "../lib/gen-api";
import { Deferred } from "../lib/promise";
import { queryKeys } from "../lib/query-keys";
import { useLibraryExternalFileUploadAction } from "./use-library-external-file-upload-action";
import { useLibraryFileUploadAction } from "./use-library-file-upload-action";

type CreateConversationOptions = {
  name: string;
  expertUuids: string[];
  prompt: string;
  folderUuids: string[];
  localFiles?: File[];
  externalFiles?: SelectedExternalFile[];
  skillUuid?: string;
};

export function useCreateConversationAction() {
  const groupUuid = useGroupUuid();
  const { createMessage } = useMessagesData(groupUuid, null, null);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const queryClient = useQueryClient();
  const { upload: uploadLibraryFile } = useLibraryFileUploadAction(
    groupUuid,
    null,
  );
  const { upload: uploadExternalFile } = useLibraryExternalFileUploadAction({
    groupUuid,
    kitUuid: null,
  });
  const { capabilities, modelProfileUuid } = useAssistContext();

  async function create({
    name,
    expertUuids,
    prompt,
    folderUuids,
    localFiles = [],
    externalFiles = [],
    skillUuid,
  }: CreateConversationOptions): Promise<{
    kitUuid: string;
    chatUuid: string;
  }> {
    setIsCreatingConversation(true);

    try {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      // 1. Create Kit
      const conversationKit = await groupKitControllerCreateKit({
        path: { groupUuid },
        body: {
          name,
          type: "conversation",
          expert_uuids: expertUuids,
          folder_uuids: folderUuids,
        },
      }).then((res) => res.data);

      if (!conversationKit) {
        throw new Error("Failed to create chat");
      }

      (async function updateConversationName() {
        const nameResponse =
          await groupKitChatControllerGenerateConversationName({
            path: { groupUuid, kitUuid: conversationKit.uuid },
            body: { message: prompt },
          });

        const generatedName = nameResponse.data?.name;
        if (generatedName && generatedName !== name) {
          await groupKitControllerUpdateKit({
            path: { groupUuid, kitUuid: conversationKit.uuid },
            body: { name: generatedName },
          });
        }

        queryClient.invalidateQueries({
          queryKey: queryKeys.kits(groupUuid),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.kit(groupUuid, conversationKit.uuid),
        });
      })();

      // 2. Create Chat
      const chat = await groupKitChatControllerCreateChat({
        path: { kitUuid: conversationKit.uuid, groupUuid },
        body: { name: getInitialChatName(), expertUuids },
      }).then((res) => res.data);

      if (!chat) throw new Error("Failed to create chat");

      let fileUuids: string[] = [];

      // 3. Upload library files and attach to chat
      if (localFiles.length > 0) {
        const localFileUuids = await Promise.all(
          localFiles.map((file) =>
            uploadLibraryFile({ kitUuid: conversationKit.uuid, file }).then(
              (r) => r.uuid,
            ),
          ),
        );
        fileUuids.push(...localFileUuids);
      }

      // 4. Upload external files and attach to chat
      if (externalFiles.length > 0) {
        const externalFileUuids = await Promise.all(
          externalFiles.map((file) =>
            uploadExternalFile({
              kitUuid: conversationKit.uuid,
              file,
            }).then((r) => r.uuid),
          ),
        );
        fileUuids.push(...externalFileUuids);
      }

      // 5. Attach files to chat
      await Promise.all(
        fileUuids.map((fileUuid) =>
          chatLibraryFileControllerAttachFile({
            path: { fileUuid, chatUuid: chat.uuid },
            throwOnError: true,
          }),
        ),
      );

      // 6. Create Message
      const isInstantiated = new Deferred<void>();
      createMessage({
        prompt,
        capabilities,
        skillUuid,
        overwriteChatUuid: chat.uuid,
        overwriteKitUuid: conversationKit.uuid,
        onInitiated: () => {
          isInstantiated.resolve();
        },
        modelProfileUuid,
      });

      await isInstantiated.promise;

      queryClient.invalidateQueries({
        queryKey: queryKeys.kits(groupUuid!, { type: "conversation" }),
      });

      return { kitUuid: conversationKit.uuid, chatUuid: chat.uuid };
    } catch (error) {
      console.error(error);
      toast.error("Failed to create chat", {
        description: error instanceof Error ? error.message : "",
      });
      throw error;
    } finally {
      setIsCreatingConversation(false);
    }
  }

  return { create, isCreatingConversation };
}
